// entities/product/lib/alphaTrimBox.ts
//
// Замер «полезной» части PNG блюда: у фото на подложке вокруг самого блюда
// почти всегда широкие прозрачные поля, из-за которых object-contain показывает
// товар мелким. Здесь картинка один раз прогоняется через canvas, и по альфа-каналу
// вычисляется прямоугольник реально видимых пикселей — дальше карточка масштабирует
// блюдо по нему, а не по формальным размерам файла.

/** Границы непрозрачных пикселей в долях от размеров картинки (0..1). */
export interface AlphaTrimBox {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Соотношение сторон исходника (naturalWidth / naturalHeight) — нужно для contain-математики. */
  aspect: number;
}

/**
 * Сторона канваса для скана. Точность границы ~1/64 размера картинки — для
 * масштабирования плитки этого достаточно, а скан остаётся дешёвым даже
 * на каталоге в сотню карточек.
 */
const SCAN_SIZE = 64;

/** Пиксели прозрачнее этого порога считаем фоном: мягкие тени у блюд почти не режутся. */
const ALPHA_THRESHOLD = 8;

/** Один URL сканируется один раз на всю страницу — карточки часто повторяются. */
const cache = new Map<string, Promise<AlphaTrimBox | null>>();

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    // Без CORS-загрузки canvas становится «tainted» и альфу прочитать нельзя.
    img.crossOrigin = 'anonymous';
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () => reject(new Error(`image load failed: ${url}`)));
    img.src = url;
  });

interface OpaqueBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Индексы крайних непрозрачных пикселей на канвасе. `null` — картинка целиком прозрачна. */
const findOpaqueBounds = (data: Uint8ClampedArray): OpaqueBounds | null => {
  let minX = SCAN_SIZE;
  let minY = SCAN_SIZE;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < SCAN_SIZE; y++) {
    for (let x = 0; x < SCAN_SIZE; x++) {
      if (data[(y * SCAN_SIZE + x) * 4 + 3] > ALPHA_THRESHOLD) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  return maxX < minX ? null : { minX, minY, maxX, maxY };
};

/**
 * Чистая математика замера: RGBA-буфер канваса SCAN_SIZE×SCAN_SIZE → границы в долях.
 * Вынесена из scan() ради юнит-тестов на синтетических буферах — canvas в них не нужен.
 * `null` — картинка целиком прозрачна: поведение карточки остаётся прежним.
 */
export const computeAlphaTrimBox = (
  data: Uint8ClampedArray,
  aspect: number,
): AlphaTrimBox | null => {
  const bounds = findOpaqueBounds(data);
  if (!bounds) return null;

  // +1 к правой/нижней границе: max — индекс последнего непрозрачного пикселя.
  return {
    x: bounds.minX / SCAN_SIZE,
    y: bounds.minY / SCAN_SIZE,
    w: (bounds.maxX - bounds.minX + 1) / SCAN_SIZE,
    h: (bounds.maxY - bounds.minY + 1) / SCAN_SIZE,
    aspect,
  };
};

const scan = async (url: string): Promise<AlphaTrimBox | null> => {
  const img = await loadImage(url);
  const { naturalWidth, naturalHeight } = img;
  if (!naturalWidth || !naturalHeight) return null;

  const canvas = document.createElement('canvas');
  // Квадратный канвас со «сплющиванием» картинки: границы считаются в долях,
  // поэтому искажение пропорций на результат не влияет.
  canvas.width = SCAN_SIZE;
  canvas.height = SCAN_SIZE;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  ctx.drawImage(img, 0, 0, SCAN_SIZE, SCAN_SIZE);
  // Бросает на tainted canvas, если сервер картинок не отдал CORS-заголовки.
  const { data } = ctx.getImageData(0, 0, SCAN_SIZE, SCAN_SIZE);

  return computeAlphaTrimBox(data, naturalWidth / naturalHeight);
};

/**
 * Границы блюда на картинке. `null` — замерить не вышло (SSR, CORS без
 * заголовков, битый файл): карточка в этом случае показывает фото как раньше.
 */
export const loadAlphaTrimBox = (url: string): Promise<AlphaTrimBox | null> => {
  if (typeof window === 'undefined' || !url) return Promise.resolve(null);
  let pending = cache.get(url);
  if (!pending) {
    pending = scan(url).catch(() => null);
    cache.set(url, pending);
  }
  return pending;
};
